// src/components/Navbar.js
import React from 'react';
import { Navbar, Nav, Container, NavDropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import NotificationDropdown from './NotificationDropdown';
import UserAvatar from './UserAvatar';

const AppNavbar = ({ darkMode, toggleDarkMode, toggleAiPopup }) => {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation(['menu', 'common']);

  const handleLogout = () => {
    logout();
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  return (
    <Navbar expand="lg" className={`w-100 border-bottom py-2 px-3 ${darkMode ? 'bg-body border-secondary' : 'bg-body border-light-subtle'}`}>
      <Container fluid className="d-flex justify-content-end p-0">
        <Nav className="justify-content-end align-items-center gap-2 gap-md-3">

          {/* Language Switcher Segment */}
          <div className={`d-flex align-items-center p-1 rounded-pill border ${darkMode ? 'bg-secondary bg-opacity-10 border-secondary' : 'bg-body-tertiary border-light-subtle'}`} style={{ height: '38px' }}>
            <button
              type="button"
              className={`btn btn-sm rounded-pill px-3 py-1 border-0 fw-semibold transition-all ${i18n.language === 'th' ? 'bg-primary text-white shadow-sm' : 'text-body-secondary'}`}
              style={{ fontSize: '0.75rem' }}
              onClick={() => changeLanguage('th')}
            >
              TH
            </button>
            <button
              type="button"
              className={`btn btn-sm rounded-pill px-3 py-1 border-0 fw-semibold transition-all ${i18n.language.startsWith('en') ? 'bg-primary text-white shadow-sm' : 'text-body-secondary'}`}
              style={{ fontSize: '0.75rem' }}
              onClick={() => changeLanguage('en')}
            >
              EN
            </button>
          </div>

          {/* Dark Mode Toggle */}
          <button
            type="button"
            className={`btn btn-sm rounded-circle d-flex align-items-center justify-content-center transition-all ${darkMode
              ? 'btn-outline-secondary text-warning border-secondary'
              : 'bg-body-tertiary text-primary border border-light-subtle'
              }`}
            style={{ width: '38px', height: '38px' }}
            onClick={toggleDarkMode}
            title={`Switch to ${darkMode ? 'Light' : 'Dark'} Mode`}
          >
            <i className={`bi ${darkMode ? 'bi-moon-stars-fill' : 'bi-sun-fill'} fs-6`}></i>
          </button>

          {/* AI Assistant Button */}
          <button
            type="button"
            className={`btn btn-sm rounded-circle d-flex align-items-center justify-content-center transition-all ${darkMode
              ? 'btn-outline-secondary text-info border-secondary'
              : 'bg-body-tertiary text-primary border border-light-subtle'
              }`}
            style={{ width: '38px', height: '38px' }}
            onClick={toggleAiPopup}
            title="AI Assistant"
          >
            <i className="bi bi-robot fs-6"></i>
          </button>

          {/* Notification Dropdown */}
          <NotificationDropdown darkMode={darkMode} size={38} />

          {/* User Profile Pill Trigger & Menu */}
          <NavDropdown
            title={
              <div className={`d-flex align-items-center gap-2 p-1 pe-3 rounded-pill transition-all cursor-pointer border ${darkMode ? 'bg-secondary bg-opacity-10 border-secondary' : 'bg-body-tertiary border-light-subtle'}`}>
                <UserAvatar user={user} size={32} fontSize={13} className="rounded-circle flex-shrink-0" />
                <div className="d-flex flex-column text-start overflow-hidden" style={{ lineHeight: '1.2' }}>
                  <span className="fw-semibold text-body text-truncate" style={{ fontSize: '0.82rem', maxWidth: '110px' }}>
                    {user?.first_name || 'User'}
                  </span>
                  <span className="text-body-secondary text-truncate" style={{ fontSize: '0.7rem' }}>
                    {user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Staff'}
                  </span>
                </div>
                <i className="bi bi-chevron-down text-body-secondary ms-1" style={{ fontSize: '0.65rem' }}></i>
              </div>
            }
            id="user-dropdown"
            align="end"
            className="no-arrow"
          >
            <div className="px-3 py-2 d-flex align-items-center gap-3" style={{ minWidth: '220px' }}>
              <UserAvatar user={user} size={42} fontSize={16} className="rounded-circle shadow-sm flex-shrink-0" />
              <div className="d-flex flex-column overflow-hidden">
                <span className="fw-bold text-body text-truncate" style={{ fontSize: '0.88rem' }}>
                  {user?.first_name} {user?.last_name}
                </span>
                <span className="text-body-secondary text-truncate" style={{ fontSize: '0.75rem' }}>
                  {user?.email || user?.username || ''}
                </span>
                <span className="badge bg-primary-subtle text-primary border border-primary-subtle mt-1 align-self-start rounded-pill px-2" style={{ fontSize: '0.65rem' }}>
                  {user?.role ? user.role.toUpperCase() : 'USER'}
                </span>
              </div>
            </div>

            <NavDropdown.Divider className="my-2" />

            <NavDropdown.Item as={Link} to="/profile" className="d-flex align-items-center gap-2 py-2 px-3">
              <i className="bi bi-person-gear fs-6 text-primary"></i>
              <span>{t('menu:profile')}</span>
            </NavDropdown.Item>

            <NavDropdown.Divider className="my-2" />

            <NavDropdown.Item onClick={handleLogout} className="d-flex align-items-center gap-2 py-2 px-3 text-danger">
              <i className="bi bi-box-arrow-right fs-6"></i>
              <span className="fw-medium">{t('menu:logout')}</span>
            </NavDropdown.Item>
          </NavDropdown>
        </Nav>
      </Container>
    </Navbar>
  );
};

export default AppNavbar;

